import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EPackageTiers } from '@feathr/blackbox';
import { Nav, NavItem, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import { useLicense, useMinPackageTooltip } from '@feathr/extender/hooks/';
import { useAccount, useLocalUrl, useRole } from '@feathr/extender/state';

interface ITooltipMessages {
  conversions?: React.ReactNode;
  implementations?: React.ReactNode;
}

type TTooltipByFeatureReturn = ITooltipMessages | undefined;

function DataNav(): JSX.Element {
  const account = useAccount();
  const localUrl = useLocalUrl();
  const { hasImports, hasSegments, hasSegmentsSome, isSudoer } = useRole();
  const { t } = useTranslation();

  const { implementationsPageVisible } = useLicense();

  const conversionsTooltip = useMinPackageTooltip('conversionPixels', 'data');
  const implementationsTooltip = useMinPackageTooltip('conversionPixels', 'data');

  const tooltipsByFeature = useMemo((): TTooltipByFeatureReturn => {
    // If user is sudoer or has access, return no tooltips
    if (isSudoer || implementationsPageVisible) {
      return undefined;
    }
    return {
      conversions: conversionsTooltip,
      implementations: implementationsTooltip,
    };
  }, [isSudoer, implementationsPageVisible, conversionsTooltip, implementationsTooltip]);

  const exploreNav = (
    <Nav indented={true} label={t('Important groups of people navigation')} type={'vertical'}>
      <NavItem
        isForwardedFrom={localUrl('/data/explore')}
        to={localUrl('/data/explore/all/people')}
      >
        {t('All')}
      </NavItem>
      <NavItem to={localUrl('/data/explore/known/people')}>{t('Known')}</NavItem>
      <NavItem to={localUrl('/data/explore/anonymous/people')}>{t('Anonymous')}</NavItem>
    </Nav>
  );

  const pixelNav = (
    <Nav indented={true} label={t('Implementation navigation')} type={'vertical'}>
      <NavItem
        to={localUrl('/data/pixel/conversions')}
        // Only show the tooltip if the user is not a sudoer and their package does not have the feature
        tooltip={tooltipsByFeature?.conversions}
        truncate={true}
      >
        {t('Conversion Pixels')}
      </NavItem>
      <TrackBlocked
        featureName={'implementations'}
        locked={!(implementationsPageVisible || isSudoer)}
        requiredPackage={implementationsPageVisible ? undefined : EPackageTiers.Essential}
      >
        <NavItem
          to={localUrl('/data/pixel/implementations')}
          tooltip={tooltipsByFeature?.implementations}
        >
          {t('Implementations')}
        </NavItem>
      </TrackBlocked>
    </Nav>
  );

  return (
    <Nav indented={true} label={t('Community navigation')} type={'vertical'}>
      {hasSegments && (
        <NavMenu hasDefaultChild={true} menu={exploreNav} to={localUrl('/data/explore')}>
          {t('People')}
        </NavMenu>
      )}
      {(hasSegments || hasSegmentsSome) && (
        <NavItem to={localUrl('/data/segments')}>{t('Groups')}</NavItem>
      )}
      {hasSegments && account.isFalcon && (
        <NavItem to={localUrl('/data/custom-fields')}>{t('Custom Fields')}</NavItem>
      )}
      {hasImports && <NavItem to={localUrl('/data/imports')}>{t('Imports')}</NavItem>}
      <NavMenu menu={pixelNav} to={localUrl('/data/pixel')}>
        {t('Super Pixel')}
      </NavMenu>
    </Nav>
  );
}

export default DataNav;
